import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { StitchService } from '@modules/common/services/stitch.service';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { DragDataTypes, type DragData } from '@modules/drag-n-drop/types/drag-data';
import { StitchBottomBarComponent } from '@modules/elements/components/stitch-bottom-bar/stitch-bottom-bar.component';
import { validateNativeDragBatch } from '@sharedlib/common/boards/native-drag-payload';
import type { FormFolderSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import { FormFolderContextMenuDirective } from '../menus/form-folder-context-menu.directive';
import { FormsSidebarActionsService } from '../services/forms-sidebar-actions.service';
import { FormsWorkspaceStore } from '../services/forms-workspace.store';

/** BoardsFolderCard markup and native shared bar; folder mutation stays with Forms' existing CAS store. */
@Component({
  selector: 'app-forms-folder-library-card', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './forms-folder-library-card.component.html',
  styleUrls: ['../../common/components/base-stitch/base-stitch.component.less'], host: { class: 'block' },
  imports: [Button2Component, IconComponent, MatTooltipModule, DateFormatPipe, DragNDropModule,
    StitchBottomBarComponent, FormFolderContextMenuDirective],
})
export class FormsFolderLibraryCardComponent {
  public readonly folder = input.required<FormFolderSummaryDto>();
  public readonly canMove = input.required<(data: DragData) => boolean>();
  public readonly open = output<boolean>();
  public readonly move = output<DragData>();
  private readonly store = inject(FormsWorkspaceStore);
  private readonly stitch = inject(StitchService);
  private readonly toaster = inject(ToasterService);
  protected readonly folderActions = inject(FormsSidebarActionsService);
  protected readonly apply = (changes: Record<string, unknown>): void => { void this.change(changes); };
  protected readonly accepts = (data: DragData): boolean => {
    const folder = this.folder(); if (folder.deleted || folder.archived || this.store.busy() || !data?.data?.length) return false;
    if (data.type === DragDataTypes.form) return this.canMove()(data);
    if ([DragDataTypes.tag, DragDataTypes.knot, DragDataTypes.connection].some(type => type === data.type)) return data.data.length <= 150;
    return validateNativeDragBatch(data.data, { purpose: 'stitch', spaceId: folder.accountId, target: folder }).ok;
  };
  protected drop(data: DragData): void {
    if (!this.accepts(data)) return;
    if (data.type === DragDataTypes.form) this.move.emit(data);
    else this.stitch.linkDragData(this.folder(), data);
  }
  protected async change(changes: Record<string, unknown>): Promise<void> {
    const folder = this.folder(); if (!this.store.canLeaveRecord()) return;
    const open = this.store.selectedFolder();
    if (open?.id === folder.id && open?.accountId === folder.accountId) { await this.store.folderMetadata(changes); return; }
    try { await this.store.api.patchFolder(folder.accountId, folder, changes); await this.store.refreshFolders(); await this.store.refresh(); }
    catch { this.toaster.show({ text: 'Couldn’t update the folder. It is back as it was.' }); }
  }
}
